// Chart states per visit, for browsing earlier visits.
import type { Entry, Visit } from '@/types/api';

export type VisitStep = {
  visit: Visit;
  /** Entries recorded during this visit, oldest first. */
  entries: Entry[];
  /** Everything recorded up to and including this visit: the chart right after it. */
  upTo: Entry[];
};

const byTime = (a: { createdAt: string }, b: { createdAt: string }) =>
  a.createdAt.localeCompare(b.createdAt);

const sameDay = (a: string, b: string) => new Date(a).toDateString() === new Date(b).toDateString();

/**
 * The visit an entry belongs to: its visitId, else a visit on the same day
 * that started before it (an entry queued offline). -1 when there is none —
 * typically today's first entries, whose visit the loaded list does not have
 * yet. Those stay out of every past state; "now" shows them.
 */
function visitIndexOf(entry: Entry, index: Map<string, number>, ordered: Visit[]): number {
  const linked = entry.visitId ? index.get(entry.visitId) : undefined;
  if (linked !== undefined) return linked;
  let found = -1;
  ordered.forEach((v, i) => {
    if (v.startedAt <= entry.createdAt && sameDay(v.startedAt, entry.createdAt)) found = i;
  });
  return found;
}

/** Visits oldest first, each with the entries it added and the chart state after it. */
export function visitTimeline(visits: readonly Visit[], entries: readonly Entry[]): VisitStep[] {
  const ordered = [...visits].sort((a, b) => a.startedAt.localeCompare(b.startedAt));
  if (!ordered.length) return [];
  const index = new Map(ordered.map((v, i) => [v.id, i]));
  const buckets: Entry[][] = ordered.map(() => []);
  for (const e of entries) {
    const i = visitIndexOf(e, index, ordered);
    if (i >= 0) buckets[i].push(e);
  }

  const upTo: Entry[] = [];
  return ordered.map((visit, i) => {
    const own = buckets[i].sort(byTime);
    upTo.push(...own);
    return { visit, entries: own, upTo: [...upTo] };
  });
}

/** Teeth that changed in a visit (rejected entries do not count). */
export function changedTeeth(step: VisitStep): Set<number> {
  return new Set(step.entries.filter((e) => e.status !== 'REJECTED').map((e) => e.tooth));
}

/** The visit took place today: its state is the same as "now". */
export function isTodaysVisit(step: VisitStep): boolean {
  return sameDay(step.visit.startedAt, new Date().toISOString());
}
